import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalParserFormatter, ErrorHandlingService, ValidationError, FormValidationService, FormValidator, AlertService } from 'core-lib';
import { PermitService, PermitDetailsModel, CreateVehicleModel } from '../services/permit.service';
import { FileListModel, FileListItemModel } from '../components/file-upload/file-upload.component';
import * as moment from 'moment';
import * as _ from 'lodash';
import { FileUploadService } from '../services/file-upload.service';
import { ApplicationService, ApplicationLOVsResponseData, ValidateRegoReponseData } from '../services/application.service';
import { environment } from '../../environments/environment';
import { PermitDetailsVehicleItemModel } from '../services/permit.service';


@Component({
  selector: 'cli-vehicles-add',
  templateUrl: './vehicles-add.component.html',
  styleUrls: ['./vehicles-add.component.scss']
})
export class VehiclesAddComponent implements OnInit {

  public vm: VehicleViewModel;
  public validator: FormValidator;
  public permit: PermitDetailsModel;

  constructor(private permitSvc: PermitService,
    private errorHandlingSvc: ErrorHandlingService,
    private formValidationSvc: FormValidationService,
    private formatter: LocalParserFormatter,
    private fileUploadSvc: FileUploadService,
    private router: Router,
    private appSvc: ApplicationService,
    private alertSvc: AlertService,
    private route: ActivatedRoute) {

    this.vm = new VehicleViewModel();
    this.validator = new FormValidator(this.vm, this.formValidationSvc);
    this.vm.fileList = new FileListModel();

    this.vm.vehicleId = this.route.snapshot.paramMap.get('id');
    this.vm.isNew = !this.vm.vehicleId;
  }

  ngOnInit() {
    this.vm.isLoading = true;
    this.vm.isOpening = !this.permitSvc.model;


    let permit$ = this.permitSvc.getDetails().toPromise().then((data: PermitDetailsModel) => {
      this.permit = data;
      this.mapModel(this.vm, data);

      return Promise.resolve();

    },
      (e) => {

        return Promise.reject(this.errorHandlingSvc.processHttpError(e));

      });


    let lov$ = this.appSvc.getApplicationLOVs().toPromise()
      .then((data: ApplicationLOVsResponseData) => {
        this.vm.permitFee = data.permitRate;
        this.vm.usageFee = data.usageRate;


        this.vm.vehicleTypes = _.map(data.vehicleTypes, (n: any) => {
          return {
            vehicleTypeId: n.vehicleTypeId,
            description: n.description
          };
        });

        this.vm.vehicleMakes = _.map(data.vehicleMakes, (n: any) => {
          return {
            vehicleMakeId: n.vehicleMakeId,
            description: n.description
          };
        });


        this.vm.totalCost = this.vm.permitFee;
        return Promise.resolve();
      }, (e) => {

        let errorMessage = this.errorHandlingSvc.processHttpError(e);
        return Promise.reject(errorMessage);

      });

    let promises$ = [permit$, lov$];

    Promise.all(promises$).finally(() => {
      this.vm.isLoading = false;
      this.vm.isOpening = false;
    });

  }

  mapModel(vm: VehicleViewModel, data: PermitDetailsModel) {

    vm.businessName = data.permitDetails.clientName;
    vm.contactName = data.permitDetails.firstName + ' ' + data.permitDetails.lastName;
    vm.permitNo = data.permitDetails.permitNo;


    if (vm.vehicleId) {

      let v: PermitDetailsVehicleItemModel = _.find(data.vehicles,
        (s: PermitDetailsVehicleItemModel) => { return s.vehicleId == vm.vehicleId; });

      vm.regoNo = v.rego;
      vm.state = v.regoState;
      vm.capacity = v.capacity;
      vm.vehicleMakeId = v.vehicleMakeId;
      vm.vehicleTypeId = v.vehicleTypeId;

      vm.isBackflowPreventionDeviceSetup = v.isAirGap || v.isRPZD;
      vm.isAirGap = v.isAirGap;
      vm.isRPZD = v.isRPZD;
      vm.isBackflowPreventionDeviceCertified = !!v.backflowPreventionCertificateIssueDate;
      vm.backflowPreventionCertificateIssueDate = this.formatter.fromIsoString(v.backflowPreventionCertificateIssueDate);

      vm.status = v.status;

      vm.fileList.items = _.map(v.attachments, (a: any) => {
      
        let item = new FileListItemModel();
  
        item.fileId = a.fileId;
        item.fileName = a.fileName;
        item.dateCreated = moment(a.dateCreated).toDate();
        item.fullUrl = environment.downloadFileUrl + '/' + a.fileId;
        item.isRemovable = true;
        
        return item;
      });
    }

  }

  public error(fieldName: string): string {
    return this.formValidationSvc.getErrorMessage(this.vm, fieldName);
  }

  validate(): Promise<void> {

    this.vm.errors = [];

    let regoValidations = [];

    let isRego = this.validator.validateRequired('regoNo', 'Registration No');
    isRego = isRego && this.validator.validateMaxLength('regoNo', 'Registration No', 8);

    let isState = this.validator.validateRequired('state', 'State');
    isState = isState && this.validator.validateState('state', 'State');

    let isCapacity = this.validator.validateRequired('capacity', 'Capacity');
    isCapacity = isCapacity && this.validator.validateNumber('capacity', 'Capacity');
    isCapacity = isCapacity && this.validator.validateMinValue('capacity', 'Capacity', 100);
    isCapacity = isCapacity && this.validator.validateMaxValue('capacity', 'Capacity', 99999);

    this.validator.validateRequired('vehicleMakeId', 'Vehicle Make');
    this.validator.validateRequired('vehicleTypeId', 'Vehicle Type');

    this.validator.validateCustom('isBackflowPreventionDeviceSetup',
      'You need to specify if backflow prevention device was set up',
      () => {
        return this.vm.isBackflowPreventionDeviceSetup === undefined;
      });

    if (this.vm.isBackflowPreventionDeviceSetup) {

      this.validator.validateCustom('backflowDeviceType',
        'You need to specify the type of backflow prevention device',
        () => {
          return !this.vm.isAirGap && !this.vm.isRPZD;
        });

      this.validator.validateCustom('isBackflowPreventionDeviceCertified',
        'You need to specify if backflow prevention device was tested',
        () => {
          return this.vm.isBackflowPreventionDeviceCertified === undefined;
        });
    }

    if (this.vm.isBackflowPreventionDeviceSetup && this.vm.isBackflowPreventionDeviceCertified) {
      let isExpiryDate = this.validator.validateRequired('backflowPreventionCertificateIssueDate',
        'Certificate Issue Date');

      if (isExpiryDate) {

        let vd = this.formatter.toMoment(this.vm.backflowPreventionCertificateIssueDate);
        let t = moment().startOf('day').toDate();
        let t12 = moment().add(-12, 'months').startOf('day').toDate();

        let isExpiryDateValid = this.validator.validateCustom('backflowPreventionCertificateIssueDate',
          'Certificate Issue Date must be a valid date',
          () => { return vd == null || !vd.isValid(); });


        isExpiryDateValid = isExpiryDateValid && this.validator.validateCustom('backflowPreventionCertificateIssueDate',
          'Certificate Issue Date cannot be in the future',
          () => { return vd.toDate() > t; });


        isExpiryDateValid = isExpiryDateValid && this.validator.validateCustom('backflowPreventionCertificateIssueDate',
          'Certificate Issue Date cannot older than 12 months',
          () => { return vd.toDate() < t12; });
      }

      this.validator.validateCustom('uploadCertificate',
        'You need to upload a copy of certificate',
        () => {
          return this.vm.fileList.items.length === 0;
        });
    }

    if (isRego && isState) {
      regoValidations.push(this.validateRego());
    }
    if (this.vm.errors.length > 0) {
      regoValidations.push(Promise.reject('Invalid Input'));
    }

    return Promise.all(regoValidations).then((results: any[]) => {

      if (this.vm.errors.length > 0) {
        return Promise.reject('Invalid Input');
      }

      return Promise.resolve();
    },
      (e) => { return Promise.reject('Invalid Input'); });
  }

  validateRego(): Promise<void> {

    let regoNo = this.vm.regoNo;
    let state = this.vm.state;

    return this.appSvc.validateRego(regoNo, state).toPromise<ValidateRegoReponseData>().then(
      (data: ValidateRegoReponseData) => {



        let activeVehicle = _.find(data.existingVehicles, (v: any) => {

          if (v.statusCode === 'Active' && v.vehicleId !== this.vm.vehicleId) {
            return true;
          }
  
        });

        let isValid = !activeVehicle;

        this.validator.validateCustom('regoNo', 'An active record of a vehicle with this registration already exists in the system. ' +
          'Please contact support for assistance', () => !isValid);

        if (!isValid) { return Promise.reject('Invalid Input'); }

        return Promise.resolve();
      },
      (e) => {
        let err = this.errorHandlingSvc.processHttpError(e);
        return Promise.reject(err);
      }
    );
  }

  public clearDevices() {
    this.vm.isAirGap = false;
    this.vm.isRPZD = false;
  }

  public clearCerficate() {
    this.vm.backflowPreventionCertificateIssueDate = null;

    if (this.vm.fileList.items.length > 0) {
      _.forEach(this.vm.fileList.items, (f: FileListItemModel) => {
        this.fileUploadSvc.deleteFile(f.fileId).toPromise().then(() => { },
          (e) => { this.errorHandlingSvc.processHttpError(e); });
      });

      this.vm.fileList.items = [];
    }
  }

  public save(): Promise<void> {
    return this.validate().then(() => {

      let request = new CreateVehicleModel();

      request.vehicleId = this.vm.vehicleId;
      request.permitId = this.permit.permitDetails.permitId;

      request.rego = this.vm.regoNo;
      request.state = this.vm.state;
      request.capacity = this.vm.capacity;
      request.vehicleMakeId = this.vm.vehicleMakeId;
      request.vehicleTypeId = this.vm.vehicleTypeId;
      request.comment = '';

      request.isAirGap = this.vm.isAirGap;
      request.isRPZD = this.vm.isRPZD;
      request.isLogSheets = true;

      request.backFlowPreventionCertificateIssueDate = this.formatter.toIsoString(
        this.vm.backflowPreventionCertificateIssueDate);

      request.startDate = this.formatter.toIsoString(this.formatter.fromMoment(moment().startOf('day')));
      request.proRataStartDate = request.startDate;

      request.attachmentIds = _.map(this.vm.fileList.items, (f: FileListItemModel) => { return f.fileId; });

      this.vm.isLoading = true;


      let promise$: Promise<boolean>;

      if(this.vm.isNew){
        promise$ = this.permitSvc.createVehicle(request).toPromise();
      }
      else {
        promise$ = this.permitSvc.updateVehicle(request).toPromise();
      }

      return promise$
        .then(() => {

          return this.permitSvc.getDetails(true).toPromise().then(() => {
            
            let message = 'The vehicle details were successfully updated';
            if(this.vm.isNew){
              message='The new vehicle request was successfully lodged';
            }

            this.alertSvc.success(environment.TOASTR_HEADER, message, 2000);
            this.router.navigate(['app/vehicles']);
            return Promise.resolve();
          });

        },
          (e) => {
            return Promise.reject(this.errorHandlingSvc.processHttpError(e));
          }).finally(() => { this.vm.isLoading = false; });

    }, (e) => {
      return Promise.reject(e);
    });
  }

  public proceedYes() {
    this.save();
  }
  public proceedCancel() {
    this.router.navigate(['app/vehicles']);
  }

  public onFileUploaded(e: FileListItemModel) {
    let v: PermitDetailsVehicleItemModel = _.find(this.permitSvc.model.vehicles, (v: PermitDetailsVehicleItemModel) => {
      return v.vehicleId == this.vm.vehicleId;
    });

    if(!v) return;

    let a = {fileId: e.fileId,
             fileName: e.fileName,
            contentType: e.contentType,
            dateCreated: e.dateCreated};
            
    v.attachments.push(a);

  }

  public onFileRemoved(e: FileListItemModel) {
    let v: PermitDetailsVehicleItemModel = _.find(this.permitSvc.model.vehicles, (v: PermitDetailsVehicleItemModel) => {
      return v.vehicleId == this.vm.vehicleId;
    });
    
    if(!v) return;

    v.attachments = _.filter(v.attachments, (a) => {
      return a.fileId != e.fileId;
    });

  }
}

export class VehicleViewModel {
  constructor() {
  }
  public isOpening: boolean;
  public isLoading: boolean;
  public businessName: string;
  public contactName: string;
  public permitNo: string;
  public expiryDate: Date;
  public isExpired: boolean;
  public isRenewable: boolean;
  public status: string;
  public isNew: boolean;

  public vehicleId: string;
  public regoNo: string;
  public state: string;
  public capacity: number;
  public vehicleTypeId: string;
  public vehicleMakeId: string;
  public isBackflowPreventionDeviceSetup: boolean;
  public isBackflowPreventionDeviceCertified: boolean;
  public backflowPreventionCertificateIssueDate: any;
  public isRPZD: boolean;
  public isAirGap: boolean;
  public errors: ValidationError[];

  public totalCost: number;
  public permitFee: number;
  public usageFee: number;

  public vehicleTypes: [];
  public vehicleMakes: [];

  public fileList: FileListModel;
}

